/* eslint-disable @next/next/no-img-element */
"use client";

import { getProjectCardInfo } from "@/app/adapters/api";
import Link from "next/link";
import { useEffect, useState } from "react";
import ContentContainer from "../content-container/contentContainer";
import {
  formatIntCurrency,
  formatIntNumber,
} from "@/app/utils/formatNumbers";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/redux/store"


type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]
}

declare const window: WindowWithDataLayer

export default function ProjectCard(props: {
  projectId: any;
  lang: string;
  onInvestmentPage?: boolean;
  isOnImpactNetworkPage?: boolean;
  isOnAdminPage?: boolean;
}) {
  const lang = props.lang;
  const equityLabel =
    lang === "en" ? "EQUITY INVESTMENT" : "INVERSIÓN EN CAPITAL";
  const lendingLabel =
    lang === "en" ? "LENDING INVESTMENT" : "INVERSIÓN EN PRÉSTAMOS";
  const convertibleLabel =
    lang === "en" ? "CONVERTIBLE INVESTMENT" : "INVERSIÓN EN NOTA CONVERTIBLE";


  const userState = useSelector(selectUser);

  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [investPercentage, setInvestPercentage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [project, setProject] = useState<any>({});

  useEffect(() => {
    getProjectCardInfo(props.projectId)
      .then((response) => {
        if (response.data.project.imageUrl?.includes("./files/project/")) {
          setImageUrl(
            response.data.project.imageUrl?.replace(
              "./files/project/",
              `${process.env.URL}/images/projects/`
            ) || ""
          );
        } else {
          setImageUrl(response.data.project.imageUrl || "");
        }
        setInvestPercentage(
          response.data.project.currentTransactionsPercentage || 0
        );
        setProject(response.data.project);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setProgress(investPercentage), 500);
    return () => clearTimeout(timer);
  }, [investPercentage]);

  const customInvestmentLinks: Record<number, string> = {
    612: "https://goparity.com/es/project/plant-on-demand-549?utm_source=BolsaSocial&utm_medium=website&utm_campaign=BS",
    613: "https://goparity.com/es/project/fibratech-international-550?utm_source=BolsaSocial&utm_medium=website&utm_campaign=BS",
    614: "https://goparity.com/es/project/lusospace-553?utm_source=BolsaSocial&utm_medium=website&utm_campaign=BS",
    615: "https://goparity.com/es/project/cooperativa-cantanhede-solar-552?utm_source=BolsaSocial&utm_medium=website&utm_campaign=BS",
    619: "https://goparity.com/es/project/origen-suroeste-555?utm_source=BolsaSocial&utm_medium=website&utm_campaign=BS",
    620: "https://goparity.com/project/tecfys-ii-559?utm_source=site&utm_medium=Bolsa%20Social&utm_campaign=BS",
    621: "https://goparity.com/project/oceano-fresco-flat-oyster-ii-556?utm_source=site&utm_medium=Bolsa%20Social&utm_campaign=BS",
  };

  return (
    <>
      {(project.status === "accepted" || project.status === "precampaign") &&
        !props.onInvestmentPage ? (
        <Link onClick={() => window.dataLayer.push({ event: 'invertir', proyecto: project.name })}
          href={customInvestmentLinks[project.id] ?? `/investment?id=${project.id}`}>
          <AcceptedProject
            project={project}
            lang={lang}
            progress={progress}
            onInvestmentPage={props.onInvestmentPage}
            investPercentage={investPercentage}
            equityLabel={equityLabel}
            lendingLabel={lendingLabel}
            convertibleLabel={convertibleLabel}
            imageUrl={imageUrl}
            userState={userState}
          />
        </Link>
      ) : null}
      {(project.status === "accepted" || project.status === "precampaign") &&
      props.onInvestmentPage ? (
        <AcceptedProject
          project={project}
          lang={lang}
          progress={progress}
          onInvestmentPage={props.onInvestmentPage}
          investPercentage={investPercentage}
          equityLabel={equityLabel}
          lendingLabel={lendingLabel}
          convertibleLabel={convertibleLabel}
          imageUrl={imageUrl}
          userState={userState}
        />
      ) : null}
      {project.status === "completed" ? (
        <PortFolioProject
          project={project}
          projectId={props.projectId}
          lang={lang}
          progress={progress}
          investPercentage={investPercentage}
          equityLabel={equityLabel}
          lendingLabel={lendingLabel}
          convertibleLabel={convertibleLabel}
          imageUrl={imageUrl}
          isOnAdminPage={props.isOnAdminPage}
        />
      ) : null}
    </>
  )
}

function AcceptedProject(props: {
  project: any;
  lang: string;
  progress: any;
  onInvestmentPage?: boolean;
  investPercentage: any;
  equityLabel: string;
  convertibleLabel: string;
  lendingLabel: string;
  imageUrl: string;
  userState?: any;
}) {

  const BLOCKED_PROGRESS_PROJECT_IDS = [612, 613, 614, 615, 619, 620, 621];

  return (
    <div
      className={
        "group w-[320px] flex flex-col rounded-3xl shadow-md p-6 transition duration-300 hover:shadow-lg hover:scale-[1.02] " +
        (props.project.project_type === "equity" ||
        props.project.project_type === "nota-convertible"
          ? "border border-lbs-secondary-brown bg-[#FDF6F1]"
          : "border border-[#48a7cf] bg-[#98C1D3]")
      }
    >
      {/* Status Badges */}
      <div className="flex justify-between items-center mb-4">

        {/* Status of the project: precampaign or accepted */}
        {props.project.status === "precampaign" && (
          <div className="bg-primary text-white text-xs rounded-full px-3 py-1">
            {props.lang === "en" ? "PRECAMPAIGN" : "EN PRECAMPAÑA"}
          </div>
        )}
        {props.project.status === "accepted" && (
          <div className="bg-primary text-white text-xs rounded-full px-3 py-1">
            {props.lang === "en" ? "CAMPAIGN" : "EN CAMPAÑA"}
          </div>
        )}

      </div>

      {/* Project Image */}
      <div className="h-[150px] overflow-hidden rounded-md mb-4">
        <img
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          src={props.imageUrl}
          alt="Project"
        />
      </div>

      {/* Project Type */}
      {props.project.project_type === "nota-convertible" && (
        <p className="text-base font-bold text-[#915408] mb-1">
          {props.convertibleLabel}
        </p>
      )}
      {props.project.project_type === "equity" && (
        <p className="text-base font-bold text-[#915408] mb-1">
          {props.equityLabel}
        </p>
      )}
      {props.project.project_type === "lending" && (
        <p className="text-base font-bold text-[#006291] mb-1">
          {props.lendingLabel}
        </p>
      )}

      {/* Project Name */}
      <p className="text-2xl font-semibold text-gray-800 mb-2">
        {props.project.name}
      </p>

      {/* Project Description */}
      <p className="text-base text-gray-600 mb-2 line-clamp-3 leading-6">
        <ContentContainer
          label={[
            props.project?.contents?.description?.enDescription,
            props.project?.contents?.description?.esDescription,
          ]}
          lang={props.lang}
        />
      </p>

      {/* Project Goal */}
      {!BLOCKED_PROGRESS_PROJECT_IDS.includes(props.project.id) && (
        <p className={"text-2xl font-bold transition duration-700 mt-4 text-gray-800"}>
          {formatIntCurrency(props.project.goal, props.lang)}
        </p>
      )}

      {/* Progress Bar */}
      {!BLOCKED_PROGRESS_PROJECT_IDS.includes(props.project.id) &&
      (props?.userState?.role === "admin" || props.project.status !== "precampaign") && (
          <div className="flex items-center mb-4">
            <Progress value={props.progress} className="h-3 flex-1" />
            <span className="text-sm text-gray-600 ml-2">
              {formatIntNumber(props.investPercentage, props.lang)}%
            </span>
          </div>
      )}
      
      {/* Lending Project Details */}
      {props.project.project_type === "lending" && (
        <div className="pb-4 grid grid-cols-1 gap-2">
          {props.project.profit && (
            <div className="bg-gray-100 rounded-lg p-2 text-sm">
              <p>
                {props.lang === "en" ? "Interest Rate:" : "Tipo de interés:"}{" "}
                <span className="font-semibold">{props.project.profit} %</span>
              </p>
            </div>
          )}
          {props.project.months && (
            <div className="bg-gray-100 rounded-lg p-2 text-sm">
              <p>
                {props.lang === "en" ? "Term:" : "Plazo:"}{" "}
                <span className="font-semibold">{props.project.months}{" "}
                {props.lang === "en" ? "months" : "meses"}</span>
              </p>
            </div>
          )}
          {props.project.rating && (
            <div className="bg-gray-100 rounded-lg p-2 text-sm">
              <p>
                {props.lang === "en" ? "Rating:" : "Rating:"}{" "}
                <span className="font-semibold">{props.project.rating}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center">
        <p
          className={
            "text-base text-white rounded-full px-3 py-1 " +
            (props.project.project_type === "equity" || props.project.project_type === "nota-convertible"
              ? "bg-[#B89A7A]" 
              : "bg-[#5A9EB2]")
          }
        >
          {props.project.status !== 'precampaign' && props.project.remainingDays >= 0
            ? `${props.project.remainingDays} ${props.lang === "en" ? "Days remaining" : "Días restantes"}`
            : props.project.status === 'precampaign'
            ? null // or "" or a custom message
            : props.lang === "en"
            ? "Ended"
            : "Finalizado"}
        </p>
        {!props.onInvestmentPage && props.project.remainingDays > 0 && (
          <Button variant="default" className="px-4 py-2 text-base rounded-xl">
            {props.lang === "en" ? "INVEST" : "INVERTIR"}
          </Button>
        )}
      </div>
  </div>
   
  );
}

function PortFolioProject(props: {
  project: any;
  projectId: any;
  lang: string;
  progress: any;
  investPercentage: any;
  equityLabel: string;
  lendingLabel: string;
  convertibleLabel: string;
  imageUrl: string;
  isOnAdminPage?: boolean;
}) {

  const [isImpactNetwork, setIsImpactNetwork] = useState(false)

  return (
    <>
      <Link onClick={() => window.dataLayer.push({ event: 'mas-info', value: props.project.name })}
        href={`/investment?id=${props.project.id}`} target={props.isOnAdminPage ? '_blank' : '_self'}>
        <div
          className={
            "group w-[320px] flex flex-col rounded-3xl shadow-md p-6 transition duration-300 hover:shadow-lg hover:scale-[1.02] " +
            (props.project.project_type === "equity" ||
            props.project.project_type === "nota-convertible"
              ? "border border-lbs-secondary-brown bg-[#FDF6F1]"
              : "border border-[#48a7cf] bg-[#98C1D3]")
          }
        >
          {/* Status Badges */}
          <div className="flex justify-between items-center mb-4">
            {props.project.project_type === "equity" && props.project.exitProject && (
              <div className="bg-black text-white text-xs rounded-full px-3 py-1">
                {props.lang === "en" ? "EXIT PROJECT" : "PROYECTO EN EXIT"}
              </div>
            )}
            {props.project.project_type === "equity" && props.project.closed && (
              <div className="bg-gray-800 text-white text-xs rounded-full px-3 py-1">
                {props.lang === "en" ? "CLOSED PROJECT" : "PROYECTO CERRADO"}
              </div>
            )}
            {props.project.project_type === "lending" && props.project.finishedLoan && (
              <div className="bg-black rounded-[40px] py-[5px] px-[15px] flex justify-center items-center">
                <p className="text-white text-[11px]">
                  {props.lang === "en" ? "FINISHED" : "PRÉSTAMO FINALIZADO"}
                </p>
              </div>
            )}
            {props.project.project_type === "lending" && props.project.unpaidLoan && (
              <div className="bg-black rounded-[40px] py-[5px] px-[15px] flex justify-center items-center">
                <p className="text-white text-[11px]">
                  {props.lang === "en" ? "UNPAID LOAN" : "PRÉSTAMO EN MORA"}
                </p>
              </div>
            )}
            {props.project.project_type === "lending" && props.project.delayedLoan && (
              <div className="bg-black rounded-[40px] py-[5px] px-[15px] flex justify-center items-center">
                <p className="text-white text-[11px]">
                  {props.lang === "en" ? "DELAYED LOAN" : "PRÉSTAMO RETRASADO"}
                </p>
              </div>
            )}
          </div>

          {/* Project Image */}
          <div className="h-[150px] overflow-hidden rounded-md mb-4">
            <img
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              src={props.imageUrl}
              alt="Project"
            />
          </div>

          {/* Project Type */}
          {props.project.project_type === "nota-convertible" && (
            <p className="text-base font-bold text-[#915408] mb-1">
              {props.convertibleLabel}
            </p>
          )}
          {props.project.project_type === "equity" && (
            <p className="text-base font-bold text-[#915408] mb-1">
              {props.equityLabel}
            </p>
          )}
          {props.project.project_type === "lending" && (
            <p className="text-base font-bold text-[#006291] mb-1">
              {props.lendingLabel}
            </p>
          )}

          {/* Project Name */}
          <p className="text-2xl font-semibold text-gray-800 mb-2">
            {props.project.name}
          </p>

          {/* Project Description */}
          <p className="text-base text-gray-600 mb-4 line-clamp-3">
            <ContentContainer
              label={[
                props.project?.contents?.description?.enDescription,
                props.project?.contents?.description?.esDescription,
              ]}
              lang={props.lang}
            />
          </p>

          {/* Footer */}
          <div className="flex justify-between items-center">
            <p
              className={
                "text-base text-white rounded-full px-3 py-1 " +
                (props.project.project_type === "equity" || props.project.project_type === "nota-convertible"
                  ? "bg-[#B89A7A]" 
                  : "bg-[#5A9EB2]")
              }
            >
              {props.project.status !== "precampaign" && props.project.remainingDays >= 0
                ? `${props.project.remainingDays} ${
                    props.lang === "en" ? "Days remaining" : "Días restantes"
                  }`
                : props.lang === "en"
                ? "Ended"
                : "Finalizado"}
            </p>
          </div>
        </div>
      </Link>
    </>
  )
}
